import { useEffect, useRef } from "react";
import { notification } from "antd";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSocketContext } from "./index";
import useCompany from "../Hooks/company";
import { useNotificationsStore } from "./notificationsStore";
import { useNotificationSounds } from "../Hooks/useNotificationSounds";
import { playNotificationSound } from "../Utilis/playNotificationSound";
import { tableQueryKeys } from "../Hooks/configration/tableQueryKeys";
import { debugLog } from "../Utilis/debug";

// Bulk imports run as queued jobs on the backend; the API call only enqueues
// them. These socket events are the only signal that a batch actually finished,
// so this listener owns both the completion toast and the cache invalidation
// that makes the freshly imported rows show up without a manual refresh.

type ImportPayload = {
  company_id?: number | string;
  batch_id?: string;
  file_name?: string;
  total_rows?: number;
  total_jobs?: number;
  failed_jobs?: number;
};

type ImportKind =
  | "product"
  | "category"
  | "offer"
  | "attribute"
  | "extra"
  | "productUnit"
  | "productAttribute"
  | "productExtra";

type ImportMeta = {
  // Query keys invalidated when the batch completes.
  queryKeys: string[];
  // Log page listing the rows that failed, when one exists for this entity.
  failedPath?: string;
};

const IMPORT_META: Record<ImportKind, ImportMeta> = {
  product: {
    queryKeys: [tableQueryKeys.products],
    failedPath: "/logs/failed-product-imports",
  },
  category: {
    queryKeys: [tableQueryKeys.categories, tableQueryKeys.allCategories],
    failedPath: "/logs/failed-category-imports",
  },
  offer: {
    queryKeys: [tableQueryKeys.offers, tableQueryKeys.offersGroup],
    failedPath: "/logs/failed-offer-imports",
  },
  attribute: { queryKeys: [tableQueryKeys.productAttributes] },
  extra: { queryKeys: [tableQueryKeys.extras] },
  productUnit: {
    queryKeys: [tableQueryKeys.unitTypes, tableQueryKeys.products],
  },
  productAttribute: {
    queryKeys: [tableQueryKeys.productAttributes, tableQueryKeys.products],
  },
  productExtra: {
    queryKeys: [tableQueryKeys.extras, tableQueryKeys.products],
  },
};

// The backend has shipped both camelCase and snake_case names for these events
// over time, so every kind is registered under both spellings.
const EVENT_NAMES: Record<ImportKind, string[]> = {
  product: ["productImportCompleted", "product_import_completed"],
  category: ["categoryImportCompleted", "category_import_completed"],
  offer: ["offerImportCompleted", "offer_import_completed"],
  attribute: ["attributeImportCompleted", "attribute_import_completed"],
  extra: ["extraImportCompleted", "extra_import_completed"],
  productUnit: ["productUnitImportCompleted", "product_unit_import_completed"],
  productAttribute: [
    "productAttributeImportCompleted",
    "product_attribute_import_completed",
  ],
  productExtra: [
    "productExtraImportCompleted",
    "product_extra_import_completed",
  ],
};

const showBrowserNotification = (title: string, body: string) => {
  if (typeof window === "undefined") return;
  if (!("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  try {
    new Notification(title, { body });
  } catch {
    // ignore — Notification ctor can throw in iframes / safari quirks
  }
};

const normalizePayload = (arg1: any, arg2?: any): ImportPayload => {
  // Supports: ({...}) and (company_id, {...}) — some emitters prefix the
  // company id positionally the way the order events do.
  const raw =
    arg1 && typeof arg1 === "object"
      ? arg1.data ?? arg1.payload ?? arg1
      : arg2 && typeof arg2 === "object"
      ? arg2
      : {};

  return {
    company_id: raw.company_id ?? raw.companyId ?? (typeof arg1 !== "object" ? arg1 : undefined),
    batch_id: raw.batch_id ?? raw.batchId,
    file_name: raw.file_name ?? raw.fileName,
    total_rows: Number(raw.total_rows ?? raw.totalRows ?? 0),
    total_jobs: Number(raw.total_jobs ?? raw.totalJobs ?? 0),
    failed_jobs: Number(raw.failed_jobs ?? raw.failedJobs ?? 0),
  };
};

export const ImportEventsListener = () => {
  const { socket } = useSocketContext();
  const { userInfo } = useSelector((state: any) => state.setReducer);
  const { company } = useCompany();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { add: addNotification } = useNotificationsStore();
  const { data: sounds } = useNotificationSounds();

  // The socket server broadcasts a batch to every open tab/listener, and a
  // reconnect can replay it, so completed batches are remembered per session.
  const seenBatches = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!socket || !userInfo || !company) {
      debugLog(
        "[ImportEventsListener] Skipped – socket:",
        !!socket,
        "userInfo:",
        !!userInfo,
        "company:",
        !!company
      );
      return;
    }

    const currentCompanyId =
      company?.company_information?.id ??
      company?.company_information?.company_id ??
      company?.company_id ??
      company?.id ??
      userInfo?.company_id;

    // Fail closed: the socket server fans every company's events out to every
    // client, so an unresolved or mismatched company id must drop the event.
    const belongsToCompany = (companyId: ImportPayload["company_id"]) => {
      if (!currentCompanyId || !companyId) return false;
      return Number(companyId) === Number(currentCompanyId);
    };

    const entityLabel = (kind: ImportKind) =>
      t(`notifications.import.entity.${kind}`, kind);

    const handlerFor = (kind: ImportKind) => (arg1: any, arg2?: any) => {
      debugLog("[ImportEventsListener]", kind, "raw:", arg1, arg2);
      const payload = normalizePayload(arg1, arg2);
      debugLog("[ImportEventsListener]", kind, "normalized:", payload);

      if (!belongsToCompany(payload.company_id)) {
        debugLog("[ImportEventsListener] BLOCKED: company mismatch", payload.company_id, "!==", currentCompanyId);
        return;
      }

      if (payload.batch_id) {
        const dedupeKey = `${kind}:${payload.batch_id}`;
        if (seenBatches.current.has(dedupeKey)) {
          debugLog("[ImportEventsListener] duplicate batch, skipping:", dedupeKey);
          return;
        }
        seenBatches.current.add(dedupeKey);
      }

      const meta = IMPORT_META[kind];
      meta.queryKeys.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      );

      // Do not invalidate ["product"] detail queries here. An open product form
      // hydrates editors from product data; refetching would wipe unsaved edits.
      // List pages already refresh via tableQueryKeys.products above.

      const entity = entityLabel(kind);
      const fileName = payload.file_name || t("notifications.import.file", "file");
      const failed = Number(payload.failed_jobs ?? 0);

      const title = failed
        ? t("notifications.import.partialTitle", "{{entity}} import finished with errors", { entity })
        : t("notifications.import.completedTitle", "{{entity}} import completed", { entity });

      const body = failed
        ? t(
            "notifications.import.partialBody",
            "{{file}}: {{failed}} of {{jobs}} batches failed ({{rows}} rows)",
            {
              file: fileName,
              failed,
              jobs: payload.total_jobs ?? 0,
              rows: payload.total_rows ?? 0,
            }
          )
        : t(
            "notifications.import.completedBody",
            "{{file}}: {{rows}} rows imported in {{jobs}} batches",
            {
              file: fileName,
              rows: payload.total_rows ?? 0,
              jobs: payload.total_jobs ?? 0,
            }
          );

      // Only failed batches get a link, and only where a failed-rows log page
      // exists — a dead link is worse than a non-clickable toast.
      const path = failed
        ? payload.batch_id
          ? `${meta.failedPath}?batch_id=${encodeURIComponent(payload.batch_id)}`
          : meta.failedPath ?? null
        : null;

      const notify = failed ? notification.warning : notification.success;
      notify({
        message: title,
        description: body,
        duration: 0,
        onClick: () => {
          if (path) window.location.href = path;
        },
      });

      addNotification({
        kind: failed ? "warning" : "import",
        title,
        body,
        path,
      });

      playNotificationSound(sounds?.status_changed?.sound_url);
      showBrowserNotification(title, body);
    };

    const registered: { event: string; handler: (...args: any[]) => void }[] = [];

    (Object.keys(EVENT_NAMES) as ImportKind[]).forEach((kind) => {
      const handler = handlerFor(kind);
      EVENT_NAMES[kind].forEach((event) => {
        socket.on(event, handler);
        registered.push({ event, handler });
      });
    });

    debugLog("[ImportEventsListener] Registered", registered.length, "listeners. socket.id:", (socket as any)?.id);

    return () => {
      registered.forEach(({ event, handler }) => socket.off(event, handler));
    };
  }, [socket, userInfo, company, queryClient, t, addNotification, sounds]);

  return null;
};
